
import { catchError } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ExceptionService, UtilsService, PaginatedList } from '../../xform-compat';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { CONFIG } from '../../config';
import { NotificationCategory, ChannelType, Notification, UnreadNotifications } from '../../shared/models';

/**
 * Resource service, API integration layer for User Notifications
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationsSettingsService {
  private notificationsPath =  `${CONFIG.apiUrl}/notifications`;
  private notificationUnreadUpdate: Subject<any>;
  private rmsUnreaded: number;
  private crmUnreaded: number;

  constructor(
    private utilsService: UtilsService,
    private exceptionService: ExceptionService,
    private http: HttpClient,
  ) {
    this.notificationUnreadUpdate = new Subject<any>();
  }

  /**
   *
   * @param id
   * @param params
   */
  public editNotification(id: number, params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.put(`${this.notificationsPath}/${id}`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param id
   * @param params
   */
  public getCategory(id: number, params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get(`${this.notificationsPath}/categories/${id}`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param params
   */
  public getTypes(params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get(`${this.notificationsPath}/types`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param params
   */
   public getCrmTypes(params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get(`${this.notificationsPath}/rms/users/notificationType`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param id
   * @param params
   */
  public getType(id: number, params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get(`${this.notificationsPath}/types/${id}`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param params
   */
  public getCategories(params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get<Array<NotificationCategory>>(`${this.notificationsPath}/categories`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param id
   * @param params
   */
  public getUserTypes(id: number, params?: any) {
    const httpParams = this.utilsService.generateParams(params);

    return this.http.get(`${this.notificationsPath}/users/${id}/types`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param userId
   * @param notification
   */
  public updateUserNotificationType(userId: number, notification: any) {
    return this.http.put(`${this.notificationsPath}/users/${userId}/types`, notification).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   */
  public getChannelTypes() {
    return this.http.get<Array<ChannelType>>(`${this.notificationsPath}/channels`).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param id
   * @param params
   */
  public getNotificationsByUser(id: number, params?: any): Observable<PaginatedList<Notification>> {
    const httpParams = this.utilsService.generateParams(params);
    return this.http.get(`${this.notificationsPath}/rms/users/${id}`, { params: httpParams }).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param userId
   * @param notification
   */
  public updateUserNotification(userId: number, notification: any): Observable<Notification> {
    return this.http.put(`${this.notificationsPath}/${notification.id}/users/${userId}`, notification).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param userId
   * @param notificationIds
   */
  public markNotificationAsRead(userId: number, notificationIds: Array<number>): Observable<Notification> {
    return this.http.put(`${this.notificationsPath}/rms/users/${userId}/acknowledge`, {notificationIds}).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   *
   * @param id
   */
  public getUnreadNotifications(id: number, scopes: Array<string>): Observable<UnreadNotifications> {
    const url = `${this.notificationsPath}/rms/users/${id}/unread?historydays=6&scope=${scopes.join('&scope=')}`;
    return this.http.get(url).pipe(
      catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /**
   * Sends a total of unread notifications to the subscribed object on the components
   * @param unreadNotifications total os unread notifications
   * @param reduce flag to reduce the total amount of notifications by the unreadNotifications
   */
  public updateUnreadTotal(unreadNotifications: number, reduce?: boolean): void {
    this.notificationUnreadUpdate.next({ unreadNotifications, reduce });
  }

  public updateUnreadByType(value: Record<string, number>): void {
    if ('COMPANY' in value) {
      this.rmsUnreaded = value['COMPANY'];
    }
    if ('USER' in value) {
      this.crmUnreaded = value['USER'];
    }
  }

  /**
   * Observable to listen when a unread notifications is sent
   */
  public getUnreadTotal(): Observable<any> {
    return this.notificationUnreadUpdate.asObservable();
  }

  /**
   * Observable to listen when a unread notifications is sent
   */
  public getCrmUnreadTotal(): number {
    return this.crmUnreaded || 0;
  }

  /**
   * Observable to listen when a unread notifications is sent
   */
  public getRmsUnreadTotal(): number {
    return this.rmsUnreaded || 0;
  }
}
